import { useAlertContext, useFormikWithDirtyForm } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { AmbulanzResponse, AmbulanzType, UpdateAmbulanzRequest, ambulanzesApi } from "@/api";
import { AmbulanzFormValues } from "@/common/components/ambulanzes/shared/AmbulanzForm.types";
import { useAmbulanzFormOptions } from "@/common/components/ambulanzes/shared/hooks/useAmbulanzFormOptions";
import { emptyToNull } from "@/common/helper/commonHelper";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useAmbulanzEdit(ambulanz: AmbulanzResponse, onClose: () => void) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();
  const { typeOptions, validationSchema } = useAmbulanzFormOptions();

  const updateAmbulanzMutation = useMutation({
    mutationFn: (request: UpdateAmbulanzRequest) =>
      ambulanzesApi.updateAmbulanz({ ambulanzId: ambulanz.ambulanzId, updateAmbulanzRequest: request }),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Ambulanzes] });
      showSuccess(t("ambulanzes.editDialog.successfullyUpdated"));
      onClose();
    },
  });

  const formik = useFormikWithDirtyForm<AmbulanzFormValues>({
    initialValues: {
      type: ambulanz.type,
      ambulanzNumber: ambulanz.ambulanzNumber ?? "",
      clientId: ambulanz.clientId ?? null,
      clientName: ambulanz.clientName ?? null,
      phone: ambulanz.phone ?? "",
      email: ambulanz.email ?? "",
      note: ambulanz.note ?? "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      const isAmbulanz = values.type === AmbulanzType.Ambulanz;

      const request: UpdateAmbulanzRequest = {
        type: values.type,
        ambulanzNumber: isAmbulanz ? emptyToNull(values.ambulanzNumber) : null,
        phone: emptyToNull(values.phone),
        email: emptyToNull(values.email),
        note: emptyToNull(values.note),
      };

      try {
        await updateAmbulanzMutation.mutateAsync(request);
      } catch {
        // Reported by the global error handling; the dialog stays open so the input is kept.
      }
    },
  });

  return { formik, typeOptions };
}
